import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import {
  createProfileService,
  patchProfileDismissedTour,
} from '@/features/profile/profileService';
import type { ProfileData } from '@/features/profile/profileTypes';
import { profileQueryKey } from '@/features/profile/profileTypes';
import { withReturnTo } from '@/routes/returnTo';

import { TourContext } from './TourContext';
import { TourCallout } from './TourCallout';
import { TourSpotlight } from './TourSpotlight';
import { productTours } from './tourDefinitions';
import { recordDeviceDismissal, readDeviceDismissals } from './tourDismissals';
import { canAutoStartTour, getDismissedTourSet } from './tourEligibility';
import { isTargetMostlyVisible, measureElement } from './tourGeometry';
import type {
  ActiveTourState,
  TourDefinition,
  TourTargetId,
  TourTargetRect,
} from './tourTypes';
import { useTourViewport } from './useTourViewport';

interface TourProviderProps {
  children: ReactNode;
}

type TargetRegistry = Partial<Record<TourTargetId, HTMLElement>>;

function getReturnTo(location: ReturnType<typeof useLocation>) {
  return `${location.pathname}${location.search}${location.hash}`;
}

function findTour(tourId: string): TourDefinition | null {
  return productTours.find((tour) => tour.id === tourId) ?? null;
}

export function TourProvider({ children }: TourProviderProps) {
  const { getIdToken, loading, signOut, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isMobile, prefersReducedMotion } = useTourViewport();
  const kuduUserId = getKuduUserId(user) ?? user?.uid ?? null;
  const [targets, setTargets] = useState<TargetRegistry>({});
  const [activeTourState, setActiveTourState] =
    useState<ActiveTourState | null>(null);
  const [targetRect, setTargetRect] = useState<TourTargetRect | null>(null);
  const [locallyDismissedTourIds, setLocallyDismissedTourIds] = useState(
    () => new Set<string>(),
  );
  const persistedDismissalIdsRef = useRef(new Set<string>());
  // Read during render, so a tour dismissed on this device never flashes open.
  const deviceDismissedTourIds = useMemo(
    () => readDeviceDismissals(kuduUserId),
    [kuduUserId],
  );
  const knownDismissedTourIds = useMemo(
    () => new Set([...deviceDismissedTourIds, ...locallyDismissedTourIds]),
    [deviceDismissedTourIds, locallyDismissedTourIds],
  );

  const createClient = useCallback(() => {
    if (!user) {
      throw new Error('You must be signed in to dismiss product tours.');
    }

    const returnTo = getReturnTo(location);

    return createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: async () => {
        await signOut();
        navigate(withReturnTo('/login', returnTo), { replace: true });
      },
    });
  }, [getIdToken, location, navigate, signOut, user]);

  const profileQuery = useQuery<ProfileData, Error>({
    enabled: !loading && Boolean(user) && Boolean(kuduUserId),
    queryFn: async () => {
      if (!kuduUserId) {
        throw new Error('You must be signed in to use product tours.');
      }

      return createProfileService(createClient()).getProfile({
        userId: kuduUserId,
      });
    },
    queryKey: profileQueryKey(kuduUserId),
    retry: false,
  });

  const { mutate: persistDismissal } = useMutation<
    ProfileData | null,
    Error,
    { tourId: string; userId: string }
  >({
    mutationFn: ({ tourId, userId }) =>
      createProfileService(createClient()).updateDismissedTour({
        tourId,
        userId,
      }),
    onSuccess: (updatedProfile, variables) => {
      if (updatedProfile) {
        queryClient.setQueryData(
          profileQueryKey(variables.userId),
          updatedProfile,
        );
      }
    },
  });

  const registerTarget = useCallback(
    (id: TourTargetId, element: HTMLElement | null) => {
      setTargets((currentTargets) => {
        if (element) {
          if (currentTargets[id] === element) {
            return currentTargets;
          }

          return { ...currentTargets, [id]: element };
        }

        if (!currentTargets[id]) {
          return currentTargets;
        }

        const nextTargets = { ...currentTargets };
        delete nextTargets[id];
        return nextTargets;
      });
    },
    [],
  );

  const contextValue = useMemo(
    () => ({ registerTarget }),
    [registerTarget],
  );
  const activeTour = activeTourState
    ? findTour(activeTourState.tourId)
    : null;
  const activeStep = activeTour?.steps[activeTourState?.stepIndex ?? 0] ?? null;
  const activeTarget = activeStep ? targets[activeStep.targetId] ?? null : null;
  const dismissedTourIds = useMemo(
    () => getDismissedTourSet(profileQuery.data, knownDismissedTourIds),
    [knownDismissedTourIds, profileQuery.data],
  );

  useEffect(() => {
    setActiveTourState(null);
    setLocallyDismissedTourIds(new Set());
    persistedDismissalIdsRef.current = new Set();
  }, [kuduUserId]);

  // A dismissal an earlier page never got to the server goes again, once a load.
  useEffect(() => {
    if (!kuduUserId || !profileQuery.isSuccess) {
      return;
    }

    for (const tourId of deviceDismissedTourIds) {
      if (
        !profileQuery.data.dismissedTours.includes(tourId) &&
        !persistedDismissalIdsRef.current.has(tourId)
      ) {
        persistedDismissalIdsRef.current.add(tourId);
        persistDismissal({ tourId, userId: kuduUserId });
      }
    }
  }, [
    deviceDismissedTourIds,
    kuduUserId,
    persistDismissal,
    profileQuery.data,
    profileQuery.isSuccess,
  ]);

  useEffect(() => {
    if (activeTourState || loading || !user || !profileQuery.isSuccess) {
      return;
    }

    const profile = profileQuery.data;

    const candidate = productTours.find((tour) => {
      const firstStep = tour.steps[0];

      return canAutoStartTour({
        dismissedToursLoaded: true,
        isMobile,
        isTargetRegistered: Boolean(firstStep && targets[firstStep.targetId]),
        locallyDismissedTourIds: knownDismissedTourIds,
        profile,
        tour,
      });
    });

    if (candidate) {
      setActiveTourState({ stepIndex: 0, tourId: candidate.id });
    }
  }, [
    activeTourState,
    isMobile,
    knownDismissedTourIds,
    loading,
    profileQuery.data,
    profileQuery.isSuccess,
    targets,
    user,
  ]);

  useEffect(() => {
    if (!activeTarget) {
      setTargetRect(null);
      return undefined;
    }

    let animationFrameId: number | null = null;
    const measure = () => {
      const updateRect = () => setTargetRect(measureElement(activeTarget));

      if (typeof window.requestAnimationFrame === 'function') {
        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
        }

        animationFrameId = window.requestAnimationFrame(() => {
          animationFrameId = null;
          updateRect();
        });
        return;
      }

      updateRect();
    };
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measure)
        : null;

    measure();
    resizeObserver?.observe(activeTarget);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.visualViewport?.addEventListener('scroll', measure);
    window.visualViewport?.addEventListener('resize', measure);

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('scroll', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [activeTarget]);

  useEffect(() => {
    if (!activeTarget || !activeTourState) {
      return;
    }

    const rect = measureElement(activeTarget);

    if (isTargetMostlyVisible(rect)) {
      return;
    }

    activeTarget.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }, [
    activeTarget,
    activeTourState?.stepIndex,
    activeTourState?.tourId,
    prefersReducedMotion,
  ]);

  const markTourDismissed = useCallback(
    (tourId: string) => {
      setActiveTourState(null);

      if (dismissedTourIds.has(tourId)) {
        return;
      }

      if (kuduUserId) {
        recordDeviceDismissal(kuduUserId, tourId);
      }

      setLocallyDismissedTourIds((currentIds) => {
        if (currentIds.has(tourId)) {
          return currentIds;
        }

        return new Set([...currentIds, tourId]);
      });

      if (kuduUserId) {
        queryClient.setQueryData<ProfileData | undefined>(
          profileQueryKey(kuduUserId),
          (currentProfile) => patchProfileDismissedTour(currentProfile, tourId),
        );
      }

      if (!kuduUserId || persistedDismissalIdsRef.current.has(tourId)) {
        return;
      }

      persistedDismissalIdsRef.current.add(tourId);
      persistDismissal({ tourId, userId: kuduUserId });
    },
    [dismissedTourIds, kuduUserId, persistDismissal, queryClient],
  );

  const handleBack = useCallback(() => {
    setActiveTourState((currentState) => {
      if (!currentState || currentState.stepIndex === 0) {
        return currentState;
      }

      return { ...currentState, stepIndex: currentState.stepIndex - 1 };
    });
  }, []);

  const handlePrimaryAction = useCallback(() => {
    if (!activeTour || !activeTourState) {
      return;
    }

    if (activeTourState.stepIndex >= activeTour.steps.length - 1) {
      markTourDismissed(activeTour.id);
      return;
    }

    setActiveTourState({
      ...activeTourState,
      stepIndex: activeTourState.stepIndex + 1,
    });
  }, [activeTour, activeTourState, markTourDismissed]);

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {activeTour && activeStep ? (
        <>
          <TourSpotlight rect={targetRect} />
          <TourCallout
            currentStep={activeTourState?.stepIndex ?? 0}
            isMobile={isMobile}
            onBack={handleBack}
            onDismiss={() => markTourDismissed(activeTour.id)}
            onPrimaryAction={handlePrimaryAction}
            rect={targetRect}
            step={activeStep}
            target={activeTarget}
            totalSteps={activeTour.steps.length}
          />
        </>
      ) : null}
    </TourContext.Provider>
  );
}
